import * as filestack from "filestack-js"

import { filestackUploadStoreOptions } from "../../utils/filestackStoreTo"

export const BLOG_INLINE_IMAGE_MAX = [2000, 2000] as const
export const BLOG_INLINE_MAX_BYTES = 25 * 1024 * 1024

const UNSAFE_SRC = /^(blob:|data:|file:)/i
const IMG_TAG = /<img\b[^>]*>/gi
const SRC_ATTR = /\bsrc\s*=\s*(["'])([^"']*)\1/i

export function isUnsafeInlineImageSrc(src: string | null | undefined): boolean {
  if (!src || !src.trim()) return true
  return UNSAFE_SRC.test(src.trim())
}

export function filestackCdnUrl(handle: string): string {
  return `https://cdn.filestackcontent.com/${handle}`
}

export function inlineImageUrlFromPicker({
  handle,
  imageUrl
}: {
  handle?: string
  imageUrl?: string
}): string | null {
  if (imageUrl?.includes("filestackcontent.com") && !isUnsafeInlineImageSrc(imageUrl)) {
    return imageUrl
  }
  if (handle) return filestackCdnUrl(handle)
  if (imageUrl && !isUnsafeInlineImageSrc(imageUrl)) return imageUrl
  return null
}

export function imageFilesFromDataTransfer(
  dataTransfer: Pick<DataTransfer, "files" | "items"> | null
): File[] {
  if (!dataTransfer) return []
  const fromFiles = Array.from(dataTransfer.files || []).filter((file) => file.type.startsWith("image/"))
  if (fromFiles.length) return fromFiles

  const fromItems: File[] = []
  Array.from(dataTransfer.items || []).forEach((item) => {
    if (item.kind === "file" && item.type.startsWith("image/")) {
      const file = item.getAsFile()
      if (file) fromItems.push(file)
    }
  })
  return fromItems
}

export function stripUnsafeInlineImagesFromHtml(html: string): string {
  if (!html) return html
  return html.replace(IMG_TAG, (tag) => {
    const match = tag.match(SRC_ATTR)
    const src = match?.[2]
    return isUnsafeInlineImageSrc(src) ? "" : tag
  })
}

export async function uploadFileToFilestack(file: File): Promise<{ handle: string; url: string }> {
  if (file.size > BLOG_INLINE_MAX_BYTES) {
    throw new Error("Image is too large (max 25MB)")
  }

  const apiKey = process.env.FILESTACK_API_KEY
  if (!apiKey) {
    throw new Error("Filestack is not configured")
  }

  const client = filestack.init(apiKey)
  const storeOptions = filestackUploadStoreOptions()
  const result = storeOptions ? await client.upload(file, {}, storeOptions) : await client.upload(file)
  const handle = result.handle
  if (!handle) {
    throw new Error("Filestack did not return a file handle")
  }

  return {
    handle,
    url: result.url || filestackCdnUrl(handle)
  }
}
